import { ILoanSummaryDashboardClosedPOCPivotSummary, ILoanSummaryDashboardClosedPOCAggregate } from '@apply/api/src/api/loanApi';
import { BandGrid } from '@apply/components';
import React, { useEffect } from 'react';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { Toolbar } from './components/Toolbar/Toolbar';
import { BandInfo } from '@apply/components/src/components/controls/StandardGrid/BandGrid';
import { Tooltip } from 'devextreme-react/tooltip';
import { getDashboardLoanTypeName } from '../../../constants/getDashboardLoanTypeName';
import { DashboardClosedByPOCPivotMode } from '../../../constants/DashboardClosedByPOCPivotMode';
import { ILoanTypeClosedByMonthTotals } from '../../../interfaces/ILoanTypeClosedByMonthTotals';

export const DashboardClosedByPersonnel: React.FC<{
    pivotMode: DashboardClosedByPOCPivotMode;
    set_pivotMode: (mode: DashboardClosedByPOCPivotMode) => void;
    dataSource?: ILoanSummaryDashboardClosedPOCPivotSummary[];
    loanTypeClosedMonthlyTotals?: ILoanTypeClosedByMonthTotals[];
    closedMonth: string | null;
    set_closedMonth: (value: string) => void;
    dashboardClosedAvailableClosedMonths?: { key: string; display: string }[];
}> = ({
    pivotMode,
    set_pivotMode,
    dataSource = [],
    loanTypeClosedMonthlyTotals,
    closedMonth,
    set_closedMonth,
    dashboardClosedAvailableClosedMonths = [],
}) => {
    // Build BandInfo from the POC columns in the first row
    const bandInfo: BandInfo[] = React.useMemo(() => {
        if (!dataSource || dataSource.length === 0) return [];

        const firstRow = dataSource[0];
        if (!firstRow.pocColumns) return [];

        const map = new Map<number, BandInfo>();

        firstRow.pocColumns.forEach((col) => {
            const gid = col.groupId ?? 9999; // fallback group
            if (!map.has(gid)) {
                map.set(gid, {
                    id: gid,
                    display: `Group ${gid}`,
                    bandId: gid,
                    members: [],
                });
            }

            map.get(gid)!.members.push(col.pointOfContact);
        });

        return Array.from(map.values()).sort((a, b) => a.bandId - b.bandId);
    }, [dataSource]);

    useEffect(() => {
        if (
            dataSource.length > 0 &&
            dashboardClosedAvailableClosedMonths.length > 0 &&
            dashboardClosedAvailableClosedMonths === null
        ) {
            set_closedMonth(dashboardClosedAvailableClosedMonths[0].key);
        }
    }, [dataSource, dashboardClosedAvailableClosedMonths, closedMonth]);

    const activeMonthTotals = React.useMemo(() => {
        if (!closedMonth) return null;
        return loanTypeClosedMonthlyTotals?.find((t) => t.month === closedMonth) ?? null;
    }, [closedMonth, loanTypeClosedMonthlyTotals]);

    const columnValueFormat = pivotMode == DashboardClosedByPOCPivotMode.Number ? undefined : 'currency';

    const onPocHeaderCellRender = (contact: ILoanSummaryDashboardClosedPOCAggregate) => {
        const targetId = `pocClosedHeaderCell_${contact.pointOfContact}`;
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {contact.pointOfContactName}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {contact.pointOfContactFullName}
                </Tooltip>
            </>
        );
    };

    return (
        <div
            id='dashboardClosedByPersonnelGrid'
            className='loanSummaryDashboard dashboardClosedByPersonnelGrid'
        >
            <Toolbar
                pivotMode={pivotMode}
                set_pivotMode={set_pivotMode}
                closedMonth={closedMonth}
                set_closedMonth={set_closedMonth}
                dashboardClosedAvailableClosedMonths={dashboardClosedAvailableClosedMonths}
            />

            <BandGrid
                dataSource={dataSource}
                keyExpr='loanTypeId'
                defaultPageSize={10}
                pagerVisible={false}
                showBorders={true}
                showRowLines={true}
                remoteOperations={false}
                bandInfo={bandInfo}
                noDataText='No Items Found'
            >
                <Column
                    dataField='loanTypeId'
                    caption=''
                    width={140}
                    alignment='left'
                    cellRender={(e) => getDashboardLoanTypeName(e.data.loanTypeId)}
                    fixed={true}
                />
                {dataSource[0]?.pocColumns.map((c, idx) => {
                    return (
                        <Column
                            dataField={c.pointOfContact}
                            key={c.pointOfContact}
                            caption={c.pointOfContactName}
                            width={155}
                            alignment='left'
                            format={columnValueFormat}
                            headerCellRender={() => onPocHeaderCellRender(c)}
                            calculateCellValue={(row) => {
                                const poc = row.pocColumns[idx];

                                // Fallback: If no month selected → show totals
                                if (!closedMonth) {
                                    return pivotMode === DashboardClosedByPOCPivotMode.Committed ? poc.committed : poc.count;
                                }

                                // Find the selected month inside monthlyDetails
                                const md = poc.monthlyDetails?.find((m) => m.month === closedMonth);
                                if (!md) {
                                    return 0;
                                }

                                switch (pivotMode) {
                                    case DashboardClosedByPOCPivotMode.Committed:
                                        return md.committed;
                                    case DashboardClosedByPOCPivotMode.Number:
                                        return md.count;
                                    default:
                                        return null;
                                }
                            }}
                        />
                    );
                })}

                <Summary>
                    <TotalItem
                        column='loanTypeId'
                        summaryType='count'
                        displayFormat='Total'
                        showInColumn='loanTypeId'
                    />
                    {dataSource[0]?.pocColumns.map((c, idx) => (
                        <TotalItem
                            key={c.pointOfContact}
                            summaryType='sum'
                            displayFormat='{0}'
                            valueFormat={columnValueFormat}
                            column={c.pointOfContact}
                            customizeText={(e) => {
                                const total = e.value;
                                const formattedTotal = e.valueText;

                                if (total == null) return '';

                                let pct = 0;

                                if (activeMonthTotals) {
                                    switch (pivotMode) {
                                        case DashboardClosedByPOCPivotMode.Committed:
                                            pct = activeMonthTotals.committed ? (total / activeMonthTotals.committed) * 100 : 0;
                                            break;

                                        case DashboardClosedByPOCPivotMode.Number:
                                            pct = activeMonthTotals.number ? (total / activeMonthTotals.number) * 100 : 0;
                                            break;
                                    }
                                }

                                const pctRounded = Number(pct.toFixed(1)); // true rounding to 1 decimal
                                const pctText =
                                    pctRounded % 1 === 0
                                        ? pctRounded.toString() // drop .0
                                        : pctRounded.toFixed(1); // keep one decimal

                                return `${formattedTotal} \u2003${pctText}%`;
                            }}
                        />
                    ))}
                </Summary>
            </BandGrid>
        </div>
    );
};
